import {
    type AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    Input,
    type OnDestroy,
    type OnInit,
    ViewChild,
    inject,
} from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Actions } from '@ngrx/effects';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import type { PopoverDirective } from 'ngx-bootstrap/popover';
import { PopoverModule } from 'ngx-bootstrap/popover';

import { EContentSources, ELanguages } from '@common/enums';
import { TranslationLoaderService } from '@common/services/translation-loader.service';
import { TranslateModule } from '@ngx-translate/core';

const MLP_ONBOARDING_KEY = 'sp-landings-onboarded';
const WEB_ONBOARDING_KEY = 'sp-web-pages-onboard';

const POPOVER_KEY = 'partners-catalog::popover';
const popoverPrefixMap = {
    [EContentSources.MiniLanding]: 'mlp::',
    [EContentSources.Website]: 'web::',
};

const onboardKeyMap = {
    [EContentSources.MiniLanding]: MLP_ONBOARDING_KEY,
    [EContentSources.Website]: WEB_ONBOARDING_KEY,
};

type PopoverShowState = {
    count: number;
    lastShow: number;
};

type SiteLangData = {
    [key: string]: {
        siteUrl: string;
        utmCampaign: string;
    };
};

const SendPulseSiteMap: SiteLangData = {
    [ELanguages.EN]: {
        siteUrl: 'https://sendpulse.com/',
        utmCampaign: 'builder-en',
    },
    [ELanguages.ES_MX]: {
        siteUrl: 'https://sendpulse.com/latam/',
        utmCampaign: 'builder-es',
    },
    [ELanguages.FR]: {
        siteUrl: 'https://sendpulse.com/fr/',
        utmCampaign: 'builder-fr',
    },
    [ELanguages.PT_BR]: {
        siteUrl: 'https://sendpulse.com/br/',
        utmCampaign: 'builder-br',
    },
    [ELanguages.IT]: {
        siteUrl: 'https://sendpulse.com/it/',
        utmCampaign: 'builder-it',
    },
    [ELanguages.RU]: {
        siteUrl: 'https://sendpulse.com/ru/',
        utmCampaign: 'builder-ru',
    },
    [ELanguages.TR]: {
        siteUrl: 'https://sendpulse.com.tr/',
        utmCampaign: 'builder-tr',
    },
    [ELanguages.UA]: {
        siteUrl: 'https://sendpulse.com/ua/',
        utmCampaign: 'builder-ua',
    },
    [ELanguages.DE]: {
        siteUrl: 'https://sendpulse.com/de/',
        utmCampaign: 'builder-de',
    },
};

@Component({
    selector: 'sp-partners-catalog',
    templateUrl: './sp-partners-catalog.component.html',
    styleUrls: ['./sp-partners-catalog.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [PopoverModule, TranslateModule],
})
export class SpPartnersCatalogComponent implements OnInit, AfterViewInit, OnDestroy {
    private readonly actions$ = inject(Actions);
    private readonly cookieService = inject(CookieService);

    @Input() contentSource: EContentSources;
    @Input() isReadyToShow$: Observable<boolean>;
    @ViewChild('popover') popover: PopoverDirective;

    private unsubscribe$ = new Subject<void>();
    private lang = TranslationLoaderService.getCurrentLocale(this.cookieService);

    public isShineEffectEnabled = false;

    get popoverKey(): string {
        return popoverPrefixMap[this.contentSource] + POPOVER_KEY;
    }

    get onboardKey(): string {
        return onboardKeyMap[this.contentSource];
    }

    get onboardStatus(): boolean {
        return JSON.parse(localStorage.getItem(this.onboardKey)) as boolean;
    }

    get state(): PopoverShowState | null {
        return JSON.parse(localStorage.getItem(this.popoverKey)) as PopoverShowState;
    }

    get autoShowDisabled(): boolean {
        return this.state?.count === 2 || !this.onboardStatus;
    }

    get searchParamsPrefix(): string {
        return this.contentSource === EContentSources.MiniLanding ? 'mlp-' : 'blp-';
    }

    ngOnInit(): void {
        this.isReadyToShow$.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
            this.popover.delay = 1000 * 60 * 5;
            // this.popover.delay = 1000 * 5;
            this.show();

            this.unsubscribe$.next();
            this.unsubscribe$.complete();
        });

        this.isShineEffectEnabled = !JSON.parse(localStorage.getItem('sp-partners-catalog-btn-clicked'));
    }

    ngAfterViewInit(): void {
        if (this.autoShowDisabled) return;

        // const showPopover = !this.state || (this.state.count < 2 && Date.now() - this.state.lastShow > 1000 * 30);
        const showPopover =
            !this.state || (this.state.count < 2 && Date.now() - this.state.lastShow > 1000 * 60 * 60 * 24);

        if (!this.onboardStatus || !showPopover) return;

        this.popover.delay = 2000;
        this.show();
    }

    show() {
        this.popover.show();
        this.popover.delay = 0;

        this.popover.onShown.pipe(take(1)).subscribe(() => {
            const newState = {
                count: this.state?.count ? this.state.count + 1 : 1,
                lastShow: Date.now(),
            };

            localStorage.setItem(this.popoverKey, JSON.stringify(newState));
        });
    }

    open() {
        const utmCampaign = this.searchParamsPrefix + this.getUtmCampaignLink();

        const url = new URL(this.getSiteUrl());
        url.pathname += 'partner-directory';
        url.searchParams.append('utm_source', 'sendpulse-service');
        url.searchParams.append('utm_medium', 'partner-window');
        url.searchParams.append('utm_campaign', utmCampaign);

        window.open(url.toString(), '_blank');
    }

    private getUtmCampaignLink(): string {
        if (SendPulseSiteMap[this.lang]) {
            return SendPulseSiteMap[this.lang].utmCampaign;
        }

        return SendPulseSiteMap[ELanguages.EN].utmCampaign;
    }

    private getSiteUrl(): string {
        if (SendPulseSiteMap[this.lang]) {
            return SendPulseSiteMap[this.lang].siteUrl;
        }

        return SendPulseSiteMap[ELanguages.EN].siteUrl;
    }

    public click(): void {
        localStorage.setItem('sp-partners-catalog-btn-clicked', JSON.stringify(true));
        this.isShineEffectEnabled = false;
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
